import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  Optional,
} from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
  selector: '[appRegexInputValidator]',
})
export class RegexInputValidatorDirective implements AfterViewInit, OnDestroy {
  @Input() appRegexInputValidator: RegExp;
  private observer: MutationObserver;

  constructor(
    private el: ElementRef,
    @Optional() private ngControl: NgControl,
  ) {}

  @HostListener('keyup', ['$event'])
  onKeyUp(event: KeyboardEvent) {
    this.sanitizeInput(event);
  }
  @HostListener('input', ['$event'])
  onInput(event: Event) {
    this.sanitizeInput(event);
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    const pastedText = event.clipboardData.getData('text');
    return this.validateInput(pastedText, event);
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent) {
    const droppedText = event.dataTransfer.getData('text');
    return this.validateInput(droppedText, event);
  }

  private validateInput(input: string, event: Event) {
    const finalValue = this.el.nativeElement.value + input;
    if (!this.appRegexInputValidator.test(finalValue)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  private async sanitizeInput(event: Event) {
    let inputElement = this.el.nativeElement as HTMLInputElement;

    if (this.el.nativeElement.tagName === 'ION-INPUT') {
      inputElement = await (this.el.nativeElement as any).getInputElement();
    }

    const currentValue = inputElement.value;
    // Remove invalid characters
    const cleanedValue = this.sanitizeValue(currentValue);

    if (cleanedValue !== currentValue) {
      inputElement.value = cleanedValue;
      // Set the cursor position to the end of the cleaned value
      if (inputElement.setSelectionRange) {
        inputElement.setSelectionRange(
          cleanedValue.length,
          cleanedValue.length,
        );
      }

      // Prevent default to block predictive text interference
      if (event instanceof KeyboardEvent || event instanceof InputEvent) {
        event.preventDefault();
      }

      if (this.ngControl && this.ngControl.control) {
        this.ngControl.control.setValue(cleanedValue, { emitEvent: false });
      }
    }
  }

  private sanitizeValue(value: string): string {
    return value
      .split('')
      .filter(char => this.appRegexInputValidator.test(char))
      .join('');
  }

  //Fallback logic to watch if predictive text on Android overrides directive with different event (key vs input)
  ngAfterViewInit() {
    const inputElement = this.el.nativeElement as HTMLInputElement;
    // Initialize MutationObserver
    this.observer = new MutationObserver(() => {
      const cleanedValue = this.sanitizeValue(inputElement.value);
      if (cleanedValue !== inputElement.value) {
        inputElement.value = cleanedValue;
        inputElement.setSelectionRange(
          cleanedValue.length,
          cleanedValue.length,
        );
        if (this.ngControl && this.ngControl.control) {
          this.ngControl.control.setValue(cleanedValue, { emitEvent: false });
        }
      }
    });

    // Observe the input element for changes to the 'value' attribute
    this.observer.observe(inputElement, {
      attributes: true,
      attributeFilter: ['value'],
    });
  }

  ngOnDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}
